import { useContext, useMemo } from 'react'
import { products } from '@gojek/web-gopay-protos'
import { Clickstream } from '@gojek/clickstream-web'
import { detect } from 'detect-browser'
import { v4 as uuidv4 } from 'uuid'

import {
    ComponentEventsConstants,
    PageEventsConstants,
} from 'constants/clickStreamEvents'
import { getTimestampInSeconds } from 'utils/utility'
import EventsContext from 'contexts/EventsContext'
import useStore from 'hooks/useStore'

import useQueryParams from './useQueryParams'
import { asBoolean } from 'helpers/environment'
import {
    CLICKSTREAM_EVENT_UI_COMPONENT,
    CLICKSTREAM_EVENT_UI_PAGE,
} from './useEventTracker'

const { Page, Component } = products.events.ui

function useEvents() {
    const eventsContext = useContext(EventsContext)
    return eventsContext
}

function eventTrackerFactory(event: Clickstream, utm: string) {
    const browser = detect()
    const isDebugMode: boolean = asBoolean(
        process.env.REACT_APP_ENABLE_CLICKSTREAM_DEBUGGING
    )
    const [store] = useStore()
    return {
        /**
         * See https://source.golabs.io/mobile/clickstream-proto/-/blob/master/src/main/protos/web/gopay/clickstream/products/events/ui/Page.proto
         *
         * Other specs that we currently do not use:
         * web.gopay.clickstream.products.common.Product product = 1;
         *   PRODUCT_UNSPECIFIED = 0;
         *   PRODUCT_GOPAYPINJAM = 1;
         * web.gopay.clickstream.products.common.PageDetail detail = 4;
         *   web.gopay.clickstream.products.common.OnboardingDetail onboarding_detail = 1;
         *     string kyc_type = 1;
         * web.gopay.clickstream.meta.EventMeta meta = 100;
         *   string event_guid = 1;
         *   web.gopay.clickstream.meta.Device device = 2;
         *     web.gopay.clickstream.meta.Browser browser = 1;
         *     web.gopay.clickstream.meta.Customer customer = 5;
         *       int32 identity = 1;
         *       web.gopay.clickstream.products.common.LendingEntity lending_entity = 2;
         *          LENDING_ENTITY_UNSPECIFIED = 0;
         *          LENDING_ENTITY_KPI = 1;
         *          LENDING_ENTITY_MGR = 2;
         *     AppSource app_source = 6;
         *       APP_SOURCE_UNSPECIFIED = 0;
         *       APP_SOURCE_GOJEK = 1;
         *       APP_SOURCE_GOPAY = 2;
         *       APP_SOURCE_TOKOPEDIA = 3;
         */
        async trackPage(
            eventName: keyof typeof PageEventsConstants,
            options?: {
                detail?: {
                    pageName?: string
                    value?: string
                    parentComponentName?: string
                }
                linking?: {
                    userId?: string
                    referenceId?: string
                }
                payment?: {
                    type?: string
                    referenceId?: string
                    challengeType?: string
                    intent?: string
                    amount?: number
                    isSuccess?: boolean
                }
                merchant?: {
                    id?: string
                    name?: string
                    channelType?: string
                }
                error?: {
                    code: string
                    description: string
                }
                useTimestamp?: boolean
                requestId?: string
                context?: string
            }
        ) {
            const eventData = {
                eventName: PageEventsConstants[eventName],
                referrer: {
                    url: window.location.href,
                    ref: document.referrer,
                    utm,
                },
                payment: options?.payment,
                merchant: options?.merchant,
                linking: options?.linking,
                meta: {
                    eventGuid: options?.requestId ?? uuidv4(),
                    device: {
                        browser: {
                            name: browser?.name,
                            userAgent: navigator.userAgent,
                            version: browser?.version,
                        },
                    },
                    customer: {
                        identity: store.userId,
                    },
                },
                error: options?.error,
                ...(options?.useTimestamp
                    ? {
                          eventTimestamp: {
                              seconds: getTimestampInSeconds(),
                          },
                      }
                    : {}),
            }

            if (isDebugMode) {
                console.debug(
                    'clicksteam: sending page event with data:',
                    eventData
                )
            }
            window.newrelic?.addPageAction(PageEventsConstants[eventName], {
                ...eventData,
                source: CLICKSTREAM_EVENT_UI_PAGE,
            })
            return await event.track(Page.create(eventData))
        },
        /**
         * See https://source.golabs.io/mobile/clickstream-proto/-/blob/master/src/main/protos/web/gopay/clickstream/products/events/ui/Component.proto
         *
         * Other specs that we currently do not use:
         * web.gopay.clickstream.products.common.Product product = 1;
         *   PRODUCT_UNSPECIFIED = 0;
         *   PRODUCT_GOPAYPINJAM = 1;
         * web.gopay.clickstream.products.common.Action action = 3;
         *   ACTION_UNSPECIFIED = 0;
         *   ACTION_VIEWED = 1;
         *   ACTION_SCROLLED = 2;
         *   ACTION_CLOSED = 3;
         *   ACTION_CLICKED = 4;
         * web.gopay.clickstream.products.common.ComponentDetail detail = 5;
         *   string value = 1;
         *   string page_name = 2;
         *   string parent_component_name = 3;
         * web.gopay.clickstream.meta.EventMeta meta = 100;
         *   string event_guid = 1;
         *   web.gopay.clickstream.meta.Device device = 2;
         *     web.gopay.clickstream.meta.Browser browser = 1;
         *     web.gopay.clickstream.meta.Customer customer = 5;
         *       int32 identity = 1;
         *       web.gopay.clickstream.products.common.LendingEntity lending_entity = 2;
         *          LENDING_ENTITY_UNSPECIFIED = 0;
         *          LENDING_ENTITY_KPI = 1;
         *          LENDING_ENTITY_MGR = 2;
         *     AppSource app_source = 6;
         *       APP_SOURCE_UNSPECIFIED = 0;
         *       APP_SOURCE_GOJEK = 1;
         *       APP_SOURCE_GOPAY = 2;
         *       APP_SOURCE_TOKOPEDIA = 3;
         */
        async trackComponent(
            eventName: keyof typeof ComponentEventsConstants,
            action: 0 | 1 | 2 | 3 | 4 = 0,
            options?: {
                detail?: {
                    pageName?: string
                    value?: string
                    parentComponentName?: string
                }
                linking?: {
                    userId?: string
                    referenceId?: string
                }
                payment?: {
                    type?: string
                    referenceId?: string
                    challengeType?: string
                    intent?: string
                    amount?: number
                    isSuccess?: boolean
                }
                merchant?: {
                    id?: string
                    name?: string
                    channelType?: string
                }
                error?: {
                    code: string
                    description: string
                }
                useTimestamp?: boolean
                requestId?: string
                context?: string
            }
        ) {
            const eventData = {
                eventName: ComponentEventsConstants[eventName],
                referrer: {
                    url: window.location.href,
                    ref: document.referrer,
                    utm,
                },
                action,
                detail: options?.detail,
                payment: options?.payment,
                merchant: options?.merchant,
                linking: options?.linking,
                meta: {
                    eventGuid: options?.requestId ?? uuidv4(),
                    device: {
                        browser: {
                            name: browser?.name,
                            userAgent: navigator.userAgent,
                            version: browser?.version,
                        },
                    },
                    customer: {
                        identity: store.userId,
                    },
                },
                error: options?.error,
                ...(options?.useTimestamp
                    ? {
                          eventTimestamp: {
                              seconds: getTimestampInSeconds(),
                          },
                      }
                    : {}),
            }

            if (isDebugMode) {
                console.debug(
                    'clicksteam: sending component event with data:',
                    eventData
                )
            }
            window.newrelic?.addPageAction(
                ComponentEventsConstants[eventName],
                {
                    ...eventData,
                    source: CLICKSTREAM_EVENT_UI_COMPONENT,
                }
            )
            return await event.track(Component.create(eventData))
        },
    }
}

function useProductUiEvent() {
    const event = useEvents()
    const { utm_source: utm } = useQueryParams()

    const tracker = useMemo(eventTrackerFactory.bind({}, event, utm), [
        event,
        utm,
    ])

    return tracker
}

export { useProductUiEvent }

export default useEvents
